import React from "react";
import Search from "../../../common/Search/Search";
import Select from "../../../common/Select/Select";
import { BsGrid3X3Gap, BsList } from "react-icons/bs";
import { FilterIcon } from "@heroicons/react/outline";

/** Portaled react-select menus use fixed coords — close on scroll so they do not drift. */
const closeDocumentsSelectMenuOnScroll = (event) => {
  const target = event?.target;
  if (target instanceof Element) {
    if (
      target.closest('[class*="menu-list"]') ||
      target.closest('[class*="MenuList"]') ||
      target.closest('[class*="menu-portal"]')
    ) {
      return false;
    }
    if (target.closest(".custom-scroll")) {
      return true;
    }
  }
  return true;
};

const DocumentsHeader = ({
  search,
  setSearch,
  sortBy,
  setSortBy,
  filterBy,
  setFilterBy,
  viewMode,
  setViewMode,
  fileTypeOptions = [],
  showFilters = true,
  /** Hide Public/Private filters (worker My Documents has no visibility toggle). */
  hideVisibilityFilters = false,
  /** Hide Private only (e.g. black portal participant documents). */
  hidePrivateFilter = false,
}) => {
  const sortOptions = [
    { label: "Date (Newest)", value: "date_desc" },
    { label: "Date (Oldest)", value: "date_asc" },
    { label: "Name (A-Z)", value: "name_asc" },
    { label: "Name (Z-A)", value: "name_desc" },
    { label: "Type", value: "type" },
  ];

  const filterOptions = [
    { label: "All Items", value: "all" },
    { label: "Folders Only", value: "folders" },
    { label: "Files Only", value: "files" },
    { label: "Expired", value: "expired" },
    { label: "Expiring Soon", value: "expiring_soon" },
    ...(hideVisibilityFilters
      ? []
      : [
          { label: "Public", value: "public" },
          ...(hidePrivateFilter ? [] : [{ label: "Private", value: "private" }]),
        ]),
  ];

  return (
    <>
      <style>{`
        .documents-header-wrapper {
          --control-height: 40px;
        }
        .documents-header-wrapper .search-wrapper input[type="text"] {
          height: var(--control-height) !important;
          padding-top: 0.5rem !important;
          padding-bottom: 0.5rem !important;
          box-sizing: border-box !important;
        }
        .documents-header-wrapper .search-wrapper > div {
          height: var(--control-height) !important;
        }
        .documents-header-wrapper .filter-controls-wrapper [class*="control"] {
          min-height: var(--control-height) !important;
          height: var(--control-height) !important;
        }
        .documents-header-wrapper .filter-controls-wrapper [class*="ValueContainer"] {
          height: 100% !important;
          min-height: auto !important;
        }
        .documents-header-wrapper .filter-icon-wrapper {
          height: var(--control-height);
          display: flex;
          align-items: center;
        }
        .documents-header-wrapper {
          overflow: hidden;
        }
        .documents-header-wrapper .header-content {
          max-width: 100%;
          overflow: hidden;
        }
        .documents-header-wrapper .filter-controls-wrapper {
          max-width: 100%;
          overflow: hidden;
        }
        /* Fix alignment for 1024px to 1235px range */
        @media (min-width: 1024px) and (max-width: 1235px) {
          .documents-header-wrapper .header-content {
            gap: 1rem !important;
          }
          .documents-header-wrapper .filter-controls-wrapper {
            gap: 0.5rem !important;
            flex-wrap: nowrap !important;
            max-width: 100% !important;
            overflow: hidden !important;
          }
          .documents-header-wrapper .search-wrapper {
            max-width: 220px !important;
            flex-shrink: 1 !important;
          }
          .documents-header-wrapper .filter-controls-wrapper > div {
            flex-shrink: 1 !important;
          }
          .documents-header-wrapper .filter-controls-wrapper > div:first-of-type {
            max-width: 160px !important;
          }
          .documents-header-wrapper .filter-controls-wrapper > div:nth-of-type(2) {
            max-width: 140px !important;
          }
        }
      `}</style>
      <div className='relative z-0 px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-200 documents-header-wrapper'>
        <div className='flex flex-col sm:flex-row gap-4 items-center justify-between header-content min-w-0'>
          {/* Search Bar */}
          <div className='flex-1 w-full sm:w-auto min-w-[200px] lg:max-w-[220px] xl:max-w-none search-wrapper min-w-0'>
            <Search
              search={search}
              setSearch={setSearch}
              placeholder='Search files and folders...'
              customClassSearch='w-full'
              width='w-full'
            />
          </div>

          {/* Filters and View Controls */}
          <div className='flex flex-wrap sm:flex-nowrap items-center gap-3 w-full sm:w-auto filter-controls-wrapper min-w-0'>
            {showFilters && (
              <>
                {/* Filter Dropdown */}
                <div className='flex items-center gap-2 h-[40px] flex-shrink-1 min-w-0'>
                  <div className='filter-icon-wrapper flex-shrink-0'>
                    <FilterIcon className='h-5 w-5 text-gray-500 flex-shrink-0' />
                  </div>
                  <div className='h-[40px] flex items-center flex-shrink-1 min-w-0 max-w-[140px] lg:max-w-[120px] xl:max-w-[150px]'>
                    <Select
                      cssClass='w-full min-w-0 lg:min-w-[120px] xl:min-w-[150px]'
                      isSearch={false}
                      data={filterOptions}
                      selectedOption={filterBy}
                      handleChange={setFilterBy}
                      placeholder='All Items'
                      useMenuPortal={true}
                      closeMenuOnScroll={closeDocumentsSelectMenuOnScroll}
                      menuShouldScrollIntoView={false}
                    />
                  </div>
                </div>

                {/* Sort Dropdown */}
                <div className='h-[40px] flex items-center flex-shrink-1 min-w-0 max-w-[140px] lg:max-w-[120px] xl:max-w-[150px]'>
                  <Select
                    cssClass='w-full min-w-0 lg:min-w-[120px] xl:min-w-[150px]'
                    isSearch={false}
                    data={sortOptions}
                    selectedOption={sortBy}
                    handleChange={setSortBy}
                    placeholder='Date (Newest)'
                    useMenuPortal={true}
                    closeMenuOnScroll={closeDocumentsSelectMenuOnScroll}
                    menuShouldScrollIntoView={false}
                  />
                </div>
              </>
            )}

            {/* View Mode Toggle */}
            <div className='flex items-center gap-1 border border-gray-300 rounded-lg px-1 h-[40px] flex-shrink-0'>
              <button
                onClick={() => setViewMode("grid")}
                className={`h-8 w-8 flex items-center justify-center rounded transition-colors ${
                  viewMode === "grid"
                    ? "bg-blue-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                aria-label='Grid view'
                title='Grid view'
              >
                <BsGrid3X3Gap className='h-4 w-4' />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`h-8 w-8 flex items-center justify-center rounded transition-colors ${
                  viewMode === "list"
                    ? "bg-blue-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                aria-label='List view'
                title='List view'
              >
                <BsList className='h-4 w-4' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentsHeader;

